import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  clearExplicitSignOut,
  clearStoredAuthRedirect,
  getCurrentAppPath,
  hasRecentExplicitSignOut,
  isPublicAppPath,
  redirectToLogin,
} from "@/utils/authRedirect";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const Ctx = createContext<AuthCtx>({ user: null, session: null, loading: true });

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef<string | null>(null);

  // Only update user state when the user ID actually changes (prevents spurious re-renders)
  const stableSetAuth = useCallback((nextSession: Session | null) => {
    const nextUser = nextSession?.user ?? null;
    const nextId = nextUser?.id ?? null;
    setSession(nextSession);
    if (nextId !== currentUserIdRef.current) {
      currentUserIdRef.current = nextId;
      setUser(nextUser);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Development bypass - only for localhost, not deployed dev sites
    const isLocalhost = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('localhost'));

    if (import.meta.env.DEV && isLocalhost) {
      console.log('Local development mode: providing mock user');
      const mockUser = {
        id: 'd874f601-67aa-4185-ac8c-86510ed03f46', // Use the same UUID as greenrhyno@gmail.com
        email: 'dev@example.com',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        aud: 'authenticated',
        role: 'authenticated',
        app_metadata: {},
        user_metadata: {},
        identities: [],
        factors: []
      } as User;

      setUser(mockUser);
      setSession({
        access_token: 'dev-token',
        refresh_token: 'dev-refresh',
        expires_in: 3600,
        expires_at: Date.now() + 3600000,
        token_type: 'bearer',
        user: mockUser
      } as Session);
      setLoading(false);
      return;
    }

    // Production/dev site: use real auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      stableSetAuth(s ?? null);

      // Handle pending password from activation flow
      // When user signs in via magic link after activation, check if there's a password to set
      if (event === 'SIGNED_IN' && s) {
        clearExplicitSignOut();
        clearStoredAuthRedirect();

        const pendingPassword = sessionStorage.getItem('pendingPassword');
        if (pendingPassword) {
          console.log('[AuthProvider] Found pending password from activation, setting it now');
          sessionStorage.removeItem('pendingPassword'); // Clear immediately to prevent re-attempts

          try {
            const { error } = await supabase.auth.updateUser({ password: pendingPassword });
            if (error) {
              console.error('[AuthProvider] Failed to set pending password:', error.message);
            } else {
              console.log('[AuthProvider] Successfully set password from activation flow');
            }
          } catch (err) {
            console.error('[AuthProvider] Error setting pending password:', err);
          }
        }
      }

      // Redirect to home page when signed out (session expired elsewhere or explicit logout)
      if (event === 'SIGNED_OUT') {
        const currentPath = getCurrentAppPath();
        if (!isPublicAppPath(currentPath)) {
          const isExplicitSignOut = hasRecentExplicitSignOut();
          console.log('[AuthProvider] Session expired/signed out, redirecting to login', {
            currentPath,
            isExplicitSignOut,
          });
          redirectToLogin(isExplicitSignOut ? 'logout' : 'expired', {
            fromPath: currentPath,
            preservePath: !isExplicitSignOut,
          });
        }
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      stableSetAuth(data.session ?? null);
    });

    // Also check session when the page regains focus (catches logout from other tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data }) => {
          stableSetAuth(data.session ?? null);
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const value = useMemo(() => ({ user, session, loading }), [user, session, loading]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);
